import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, Edit2, Trash2, Copy, Star, Save, X, Check,
  Crown, Settings2, Zap, MapPin, Mic, Eye, TrendingUp, Layers,
  Users, Moon, Plane, Home, Sparkles, ArrowRight, ChevronDown,
  Calculator, FileText, Wand2, DollarSign, CheckCircle2, XCircle,
  Radio, Globe, Send, Newspaper, Linkedin, Camera, Award, BookOpen
} from "lucide-react";

/* ─── Section Tab Config ─── */
const TABS = [
  { id: "basic", label: "Basics", icon: Settings2 },
  { id: "foundation", label: "Foundation", icon: Crown },
  { id: "story", label: "Story Activation", icon: Mic },
  { id: "onsite", label: "On-Site", icon: MapPin },
  { id: "distribution", label: "Distribution", icon: Globe },
  { id: "recommended", label: "Recommended", icon: Layers },
  { id: "investment", label: "Investment", icon: TrendingUp },
  { id: "import", label: "AI Import", icon: Wand2 },
  { id: "settings", label: "Settings", icon: Settings2 },
];

const MODULE_ICONS = {
  podcast_content: Mic, editorial_seo: FileText, coffee_table_book: BookOpen,
  platform_placement: Globe, newsletter_feature: Newspaper, social_campaign: Send,
  linkedin_feature: Linkedin, editorial_placement: FileText,
  soulful_score: Award, onsite_storytelling: Camera, gm_story: Users, design_story: Home,
};

/* ─── Default Sections ─── */
const DEFAULT_SECTIONS = {
  foundation: {
    enabled: true,
    tiers: [
      { id: "tier_1", name: "SELECTED", price: 12500, is_recommended: false, description_en: "Curated visibility within the ecosystem", description_de: "Kuratierte Sichtbarkeit im Ökosystem" },
      { id: "tier_2", name: "SIGNATURE", price: 18000, is_recommended: true, description_en: "Enhanced narrative and strategic positioning", description_de: "Erweiterte Narrative und strategische Positionierung" },
      { id: "tier_3", name: "ICON", price: 28000, is_recommended: false, description_en: "Maximum presence and flagship positioning", description_de: "Maximale Präsenz und Flagship-Positionierung" },
    ],
    comparison_features: [],
  },
  story_activation: { enabled: true, modules: [] },
  onsite: {
    visit: { enabled: true, team_size: 4, nights: 2, hosted_by_property: true, travel_expense_mode: "covered_by_property", note_en: "", note_de: "" },
    evaluation: { enabled: false, modules: [] },
    production: { enabled: false, modules: [] },
  },
  distribution: { enabled: true, modules: [] },
  recommended_setup: { enabled: true, label_en: "Recommended Configuration", label_de: "Empfohlene Konfiguration", description_en: "", description_de: "", recommended_tier: "tier_2", preselected_modules: [] },
  investment: { show_breakdown: true, roi_enabled: true, calculator: { adr: 350, avg_stay_nights: 3, margin_percent: 30, annual_investment: 0 }, roi_scenarios: [
    { label_en: "Conservative", label_de: "Konservativ", bookings: 3, avg_value: 8000 },
    { label_en: "Moderate", label_de: "Moderat", bookings: 6, avg_value: 10000 },
    { label_en: "Optimistic", label_de: "Optimistisch", bookings: 12, avg_value: 12000 },
  ]},
  settings: { cta_primary_en: "Proceed to Partnership Setup", cta_primary_de: "Weiter zur Partnerschafts-Einrichtung", cta_secondary_en: "Request Changes", cta_secondary_de: "Änderungen anfordern", cta_call_en: "Schedule a Call", cta_call_de: "Gespräch vereinbaren" },
};

export default function TemplatesPage() {
  const { token } = useAuth();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("basic");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchTemplates(); }, [token]);

  const fetchTemplates = async () => {
    try { const { data } = await axios.get(`${API}/templates`, { headers }); setTemplates(data); }
    catch { toast.error("Failed to load templates"); }
    setLoading(false);
  };

  const handleSave = async () => {
    if (!editing?.name) { toast.error("Template name required"); return; }
    setSaving(true);
    try {
      if (editing.id) { await axios.put(`${API}/templates/${editing.id}`, editing, { headers }); toast.success("Template updated"); }
      else { await axios.post(`${API}/templates`, editing, { headers }); toast.success("Template created"); }
      fetchTemplates(); setShowEditor(false); setEditing(null);
    } catch { toast.error("Save failed"); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    try { await axios.delete(`${API}/templates/${id}`, { headers }); toast.success("Deleted"); fetchTemplates(); }
    catch { toast.error("Delete failed"); }
  };

  const openEditor = (tmpl) => {
    const s = tmpl?.sections ? JSON.parse(JSON.stringify(tmpl.sections)) : JSON.parse(JSON.stringify(DEFAULT_SECTIONS));
    setEditing({ ...tmpl, sections: s });
    setActiveTab("basic");
    setShowEditor(true);
  };

  const S = editing?.sections || {};
  const up = (key, val) => setEditing(p => ({ ...p, sections: { ...p.sections, [key]: val } }));

  // ═══════════════ LIST VIEW ═══════════════
  if (!showEditor) {
    if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><div className="spinner" /></div>;
    return (
      <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="templates-page">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10">
          <div>
            <h1 className="text-3xl font-bold text-white tracking-tight" data-testid="templates-title">Proposal Templates</h1>
            <p className="text-white/40 text-sm mt-2">Configure section-based partnership blueprints</p>
          </div>
          <button onClick={() => openEditor({ name: "", description: "", category: "premium", sections: JSON.parse(JSON.stringify(DEFAULT_SECTIONS)), is_default: false, sort_order: templates.length })}
            className="flex items-center gap-2 px-6 py-3 bg-[#C9A96E] text-[#030303] rounded-xl text-sm font-bold hover:bg-[#D4B87A] transition-all hover:shadow-lg hover:shadow-[#C9A96E]/20"
            data-testid="new-template-btn"><Plus className="w-4 h-4" />New Template</button>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
          {templates.map(t => <TemplateCard key={t.id} t={t} onEdit={() => openEditor(t)} onDelete={() => handleDelete(t.id)} onDuplicate={async () => {
            const dup = { ...t, name: `${t.name} (Copy)`, is_default: false, sections: JSON.parse(JSON.stringify(t.sections || DEFAULT_SECTIONS)) };
            delete dup.id; delete dup.created_at; delete dup.updated_at;
            try { await axios.post(`${API}/templates`, dup, { headers }); toast.success("Duplicated"); fetchTemplates(); } catch { toast.error("Failed"); }
          }} onSetDefault={async () => {
            try { await axios.put(`${API}/templates/${t.id}`, { ...t, is_default: true }, { headers }); toast.success("Set as default"); fetchTemplates(); } catch { toast.error("Failed"); }
          }} />)}
        </div>
      </div>
    );
  }

  // ═══════════════ EDITOR VIEW ═══════════════
  const foundation = S.foundation || {};
  const story = S.story_activation || {};
  const onsite = S.onsite || {};
  const distribution = S.distribution || {};
  const recommended = S.recommended_setup || {};
  const investment = S.investment || {};
  const settings = S.settings || {};

  return (
    <div className="p-6 lg:p-10 max-w-7xl mx-auto" data-testid="template-editor">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-white">{editing?.id ? "Edit Template" : "New Template"}</h1>
          <p className="text-white/35 text-sm mt-1">Section-based proposal blueprint</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => { setShowEditor(false); setEditing(null); }} className="px-5 py-2.5 text-sm text-white/50 hover:text-white border border-white/10 rounded-xl hover:border-white/20 transition-all">Cancel</button>
          <button onClick={handleSave} disabled={saving} data-testid="save-template-btn"
            className="flex items-center gap-2 px-6 py-2.5 bg-[#C9A96E] text-[#030303] rounded-xl text-sm font-bold hover:bg-[#D4B87A] transition-all hover:shadow-lg hover:shadow-[#C9A96E]/20 disabled:opacity-50">
            <Save className="w-4 h-4" />{saving ? "Saving..." : "Save Template"}
          </button>
        </div>
      </div>

      {/* Section Nav */}
      <div className="border-b border-white/[0.06] mb-8 -mx-6 lg:-mx-10 px-6 lg:px-10">
        <div className="flex gap-1 overflow-x-auto pb-px">
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`tab-${tab.id}`}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-all whitespace-nowrap ${
                activeTab === tab.id ? "text-[#C9A96E] border-[#C9A96E]" : "text-white/30 border-transparent hover:text-white/60"}`}>
              <tab.icon className="w-4 h-4" />{tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── BASIC INFO ── */}
      {activeTab === "basic" && (
        <div className="space-y-6 max-w-3xl">
          <SectionHeader title="Template Basics" sub="Name, category, and general configuration." />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <BigInput label="Template Name" value={editing?.name || ""} onChange={v => setEditing(p => ({ ...p, name: v }))} placeholder="e.g. Premium Partnership" testId="template-name" />
            <div>
              <label className="block text-white/50 text-xs font-semibold uppercase tracking-wider mb-2">Category</label>
              <select value={editing?.category || "premium"} onChange={e => setEditing(p => ({ ...p, category: e.target.value }))}
                className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3.5 text-white text-sm focus:border-[#C9A96E]/40 focus:outline-none transition-colors">
                <option value="standard">Standard</option><option value="premium">Premium</option><option value="enterprise">Enterprise</option><option value="custom">Custom</option>
              </select>
            </div>
          </div>
          <BigInput label="Description" value={editing?.description || ""} onChange={v => setEditing(p => ({ ...p, description: v }))} placeholder="Describe this template blueprint" textarea />
          <ToggleSwitch label="Set as default template for new proposals" checked={editing?.is_default || false} onChange={v => setEditing(p => ({ ...p, is_default: v }))} />
        </div>
      )}

      {/* ── FOUNDATION ── */}
      {activeTab === "foundation" && (
        <div className="space-y-8">
          <SectionHeader title="Foundation" sub="Membership tiers, annual pricing, and the comparison matrix. This is the only section where tier-based comparison logic lives." icon={Crown} />
          
          {/* Tier Cards */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            {(foundation.tiers || []).map((tier, i) => (
              <div key={tier.id} className={`relative rounded-2xl border-2 p-6 transition-all ${
                tier.is_recommended ? "border-[#C9A96E]/50 bg-gradient-to-b from-[#C9A96E]/8 to-transparent shadow-lg shadow-[#C9A96E]/5" : "border-white/[0.08] bg-white/[0.02] hover:border-white/15"}`}>
                {tier.is_recommended && <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#C9A96E] text-[#030303] text-[10px] font-bold uppercase tracking-widest rounded-full">Recommended</div>}
                <button onClick={() => { const tiers = foundation.tiers.filter((_, j) => j !== i); up("foundation", { ...foundation, tiers }); }}
                  className="absolute top-3 right-3 p-1.5 text-white/15 hover:text-red-400 transition-colors"><Trash2 className="w-4 h-4" /></button>
                
                <input value={tier.name} onChange={e => { const t = [...foundation.tiers]; t[i] = { ...t[i], name: e.target.value }; up("foundation", { ...foundation, tiers: t }); }}
                  className="w-full bg-transparent text-white text-xl font-bold tracking-wider text-center border-none outline-none placeholder:text-white/15 mb-4" placeholder="TIER NAME" />
                
                <div className="flex items-baseline justify-center gap-1 mb-4">
                  <span className="text-white/30 text-sm">EUR</span>
                  <input type="number" value={tier.price} onChange={e => { const t = [...foundation.tiers]; t[i] = { ...t[i], price: parseFloat(e.target.value) || 0 }; up("foundation", { ...foundation, tiers: t }); }}
                    className="bg-transparent text-white text-3xl font-light text-center border-none outline-none w-32" />
                  <span className="text-white/30 text-sm">/ year</span>
                </div>
                
                <textarea value={tier.description_en || ""} onChange={e => { const t = [...foundation.tiers]; t[i] = { ...t[i], description_en: e.target.value }; up("foundation", { ...foundation, tiers: t }); }}
                  className="w-full bg-white/[0.03] border border-white/[0.06] rounded-lg px-3 py-2 text-white/60 text-sm resize-none h-16 focus:border-[#C9A96E]/30 focus:outline-none mb-3" placeholder="Short description..." />
                
                <button onClick={() => { const t = foundation.tiers.map((ti, j) => ({ ...ti, is_recommended: j === i ? !ti.is_recommended : false })); up("foundation", { ...foundation, tiers: t }); }}
                  className={`w-full py-2 rounded-lg text-xs font-semibold transition-all ${
                    tier.is_recommended ? "bg-[#C9A96E]/20 text-[#C9A96E]" : "bg-white/[0.03] text-white/25 hover:text-white/50"}`}>
                  <Star className="w-3 h-3 inline mr-1" />{tier.is_recommended ? "Recommended" : "Set as recommended"}
                </button>
              </div>
            ))}
            <button onClick={() => {
              const newId = `tier_${Date.now()}`;
              up("foundation", { ...foundation, tiers: [...(foundation.tiers || []), { id: newId, name: "", price: 0, is_recommended: false, description_en: "", description_de: "" }] });
            }} className="flex flex-col items-center justify-center min-h-[280px] rounded-2xl border-2 border-dashed border-white/[0.08] text-white/20 hover:border-[#C9A96E]/30 hover:text-[#C9A96E] transition-all cursor-pointer">
              <Plus className="w-8 h-8 mb-2" /><span className="text-sm font-medium">Add Tier</span>
            </button>
          </div>

          {/* Comparison Matrix */}
          <div className="mt-10">
            <SectionHeader title="Comparison Matrix" sub="Toggle which features are included in each tier." />
            <div className="rounded-2xl border border-white/[0.06] overflow-hidden">
              {/* Header */}
              <div className="grid bg-white/[0.03] border-b border-white/[0.06]" style={{ gridTemplateColumns: `1fr ${(foundation.tiers || []).map(() => '100px').join(' ')}` }}>
                <div className="px-5 py-3 text-white/30 text-xs font-semibold uppercase tracking-wider">Feature</div>
                {(foundation.tiers || []).map(t => <div key={t.id} className="px-3 py-3 text-center text-white/40 text-xs font-semibold uppercase tracking-wider">{t.name?.substring(0, 6)}</div>)}
              </div>
              {/* Rows */}
              {(foundation.comparison_features || []).map((f, i) => (
                <div key={i} className="grid items-center border-b border-white/[0.03] hover:bg-white/[0.02] transition-colors" style={{ gridTemplateColumns: `1fr ${(foundation.tiers || []).map(() => '100px').join(' ')}` }}>
                  <div className="px-5 py-3 flex items-center gap-2">
                    <input value={f.name_en || ""} onChange={e => { const fs = [...(foundation.comparison_features || [])]; fs[i] = { ...fs[i], name_en: e.target.value }; up("foundation", { ...foundation, comparison_features: fs }); }}
                      className="bg-transparent text-white/70 text-sm border-none outline-none flex-1 placeholder:text-white/15" placeholder="Feature name" />
                    <button onClick={() => up("foundation", { ...foundation, comparison_features: (foundation.comparison_features || []).filter((_, j) => j !== i) })}
                      className="p-1 text-white/10 hover:text-red-400 transition-colors shrink-0"><X className="w-3.5 h-3.5" /></button>
                  </div>
                  {(foundation.tiers || []).map(t => (
                    <div key={t.id} className="flex justify-center py-3">
                      <button onClick={() => { const fs = [...(foundation.comparison_features || [])]; fs[i] = { ...fs[i], [t.id]: !fs[i][t.id] }; up("foundation", { ...foundation, comparison_features: fs }); }}
                        className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${f[t.id] ? "bg-[#C9A96E]/20 text-[#C9A96E]" : "bg-white/[0.03] text-white/10 hover:text-white/30"}`}>
                        {f[t.id] ? <Check className="w-4 h-4" /> : <X className="w-3.5 h-3.5" />}
                      </button>
                    </div>
                  ))}
                </div>
              ))}
              {/* Add Row */}
              <button onClick={() => {
                const nf = { name_en: "", name_de: "" }; (foundation.tiers || []).forEach(t => { nf[t.id] = false; });
                up("foundation", { ...foundation, comparison_features: [...(foundation.comparison_features || []), nf] });
              }} className="w-full px-5 py-3 text-left text-[#C9A96E]/50 text-sm hover:text-[#C9A96E] hover:bg-[#C9A96E]/5 transition-all">
                <Plus className="w-4 h-4 inline mr-2" />Add Feature Row
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── STORY ACTIVATION ── */}
      {activeTab === "story" && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <SectionHeader title="Story Activation" sub="Remote storytelling modules — podcasts, editorial, SEO, coffee table book features." icon={Mic} />
            <ToggleSwitch label="Enabled" checked={story.enabled !== false} onChange={v => up("story_activation", { ...story, enabled: v })} />
          </div>
          <ModuleGrid modules={story.modules || []} onChange={m => up("story_activation", { ...story, modules: m })} tiers={foundation.tiers || []} />
        </div>
      )}

      {/* ── ON-SITE EXPERIENCE ── */}
      {activeTab === "onsite" && (
        <div className="space-y-10">
          {/* Visit */}
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <SectionHeader title="On-Site Visit" sub="Team visit to the property — hosted, all-inclusive experience." icon={MapPin} />
              <ToggleSwitch label="Enabled" checked={onsite.visit?.enabled !== false} onChange={v => up("onsite", { ...onsite, visit: { ...onsite.visit, enabled: v } })} />
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard label="Team Members" value={onsite.visit?.team_size ?? 4} icon={Users}
                onChange={v => up("onsite", { ...onsite, visit: { ...onsite.visit, team_size: parseInt(v) || 4 } })} />
              <StatCard label="Nights" value={onsite.visit?.nights ?? 2} icon={Moon}
                onChange={v => up("onsite", { ...onsite, visit: { ...onsite.visit, nights: parseInt(v) || 2 } })} />
              <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5">
                <Plane className="w-5 h-5 text-[#C9A96E]/40 mb-3" />
                <label className="text-white/30 text-[10px] font-semibold uppercase tracking-wider">Travel Expenses</label>
                <select value={onsite.visit?.travel_expense_mode || "covered_by_property"} onChange={e => up("onsite", { ...onsite, visit: { ...onsite.visit, travel_expense_mode: e.target.value } })}
                  className="w-full bg-transparent text-white text-sm border-none outline-none mt-1 cursor-pointer">
                  <option value="covered_by_property">Covered by Property</option><option value="waived_if_on_route">Waived if on Route</option><option value="shared">Shared</option>
                </select>
              </div>
              <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5">
                <Home className="w-5 h-5 text-[#C9A96E]/40 mb-3" />
                <label className="text-white/30 text-[10px] font-semibold uppercase tracking-wider">Accommodation</label>
                <select value={onsite.visit?.hosted_by_property ? "hosted" : "self"} onChange={e => up("onsite", { ...onsite, visit: { ...onsite.visit, hosted_by_property: e.target.value === "hosted" } })}
                  className="w-full bg-transparent text-white text-sm border-none outline-none mt-1 cursor-pointer">
                  <option value="hosted">Hosted by Property</option><option value="self">Self-Arranged</option>
                </select>
              </div>
            </div>
          </div>

          {/* Evaluation */}
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <SectionHeader title="On-Site Evaluation" sub="Soulful Score assessment, quality report, strategic insights." icon={Award} />
              <ToggleSwitch label="Enabled" checked={onsite.evaluation?.enabled === true} onChange={v => up("onsite", { ...onsite, evaluation: { ...onsite.evaluation, enabled: v } })} />
            </div>
            {onsite.evaluation?.enabled && <ModuleGrid modules={onsite.evaluation?.modules || []} onChange={m => up("onsite", { ...onsite, evaluation: { ...onsite.evaluation, modules: m } })} tiers={foundation.tiers || []} />}
          </div>

          {/* Production */}
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <SectionHeader title="On-Site Production" sub="Cinematic storytelling — founder portraits, design stories, culinary features." icon={Camera} />
              <ToggleSwitch label="Enabled" checked={onsite.production?.enabled === true} onChange={v => up("onsite", { ...onsite, production: { ...onsite.production, enabled: v } })} />
            </div>
            {onsite.production?.enabled && <ModuleGrid modules={onsite.production?.modules || []} onChange={m => up("onsite", { ...onsite, production: { ...onsite.production, modules: m } })} tiers={foundation.tiers || []} />}
          </div>
        </div>
      )}

      {/* ── DISTRIBUTION ── */}
      {activeTab === "distribution" && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <SectionHeader title="Visibility & Distribution" sub="Platform, newsletter, social media, LinkedIn, and editorial placement modules." icon={Globe} />
            <ToggleSwitch label="Enabled" checked={distribution.enabled !== false} onChange={v => up("distribution", { ...distribution, enabled: v })} />
          </div>
          <ModuleGrid modules={distribution.modules || []} onChange={m => up("distribution", { ...distribution, modules: m })} tiers={foundation.tiers || []} showIncluded />
        </div>
      )}

      {/* ── RECOMMENDED SETUP ── */}
      {activeTab === "recommended" && (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <SectionHeader title="Recommended Setup" sub="A curated shortcut — the ideal collaboration configuration for this property type." icon={Layers} />
            <ToggleSwitch label="Enabled" checked={recommended.enabled !== false} onChange={v => up("recommended_setup", { ...recommended, enabled: v })} />
          </div>
          <div className="rounded-2xl border-2 border-[#C9A96E]/20 bg-gradient-to-br from-[#C9A96E]/5 to-transparent p-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-[#C9A96E]/15 flex items-center justify-center shrink-0"><Sparkles className="w-6 h-6 text-[#C9A96E]" /></div>
              <div className="flex-1">
                <BigInput label="Title (EN)" value={recommended.label_en || ""} onChange={v => up("recommended_setup", { ...recommended, label_en: v })} placeholder="Recommended Configuration" />
                <div className="mt-3">
                  <BigInput label="Description (EN)" value={recommended.description_en || ""} onChange={v => up("recommended_setup", { ...recommended, description_en: v })} placeholder="A curated selection designed to maximize impact..." textarea />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-6">
              <div>
                <label className="block text-white/40 text-xs font-semibold uppercase tracking-wider mb-2">Recommended Tier</label>
                <select value={recommended.recommended_tier || ""} onChange={e => up("recommended_setup", { ...recommended, recommended_tier: e.target.value })}
                  className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:border-[#C9A96E]/40 focus:outline-none">
                  <option value="">None</option>
                  {(foundation.tiers || []).map(t => <option key={t.id} value={t.id}>{t.name} — EUR {t.price?.toLocaleString("de-DE")}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-white/40 text-xs font-semibold uppercase tracking-wider mb-2">Pre-Selected Modules</label>
                <div className="flex flex-wrap gap-2">
                  {getAllModules(S).map(m => (
                    <button key={m.id} onClick={() => {
                      const cur = recommended.preselected_modules || [];
                      up("recommended_setup", { ...recommended, preselected_modules: cur.includes(m.id) ? cur.filter(x => x !== m.id) : [...cur, m.id] });
                    }} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                      (recommended.preselected_modules || []).includes(m.id) ? "bg-[#C9A96E]/20 text-[#C9A96E] border border-[#C9A96E]/30" : "bg-white/[0.03] text-white/30 border border-white/[0.06] hover:text-white/60"}`}>
                      {m.name_en}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── INVESTMENT / ROI ── */}
      {activeTab === "investment" && (
        <div className="space-y-8">
          <SectionHeader title="Investment & ROI Calculator" sub="Pricing summary, recovery scenarios, and required bookings. All values are stored as a snapshot." icon={TrendingUp} />
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Calculator Inputs */}
            <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 space-y-5">
              <h3 className="text-white font-semibold text-sm flex items-center gap-2"><Calculator className="w-4 h-4 text-[#C9A96E]" />Calculator Settings</h3>
              <CalcField label="Average Daily Rate (ADR)" value={investment.calculator?.adr ?? 350} suffix="EUR" onChange={v => up("investment", { ...investment, calculator: { ...(investment.calculator || {}), adr: v } })} />
              <CalcField label="Average Stay (Nights)" value={investment.calculator?.avg_stay_nights ?? 3} suffix="nights" onChange={v => up("investment", { ...investment, calculator: { ...(investment.calculator || {}), avg_stay_nights: v } })} />
              <CalcField label="Margin Assumption" value={investment.calculator?.margin_percent ?? 30} suffix="%" onChange={v => up("investment", { ...investment, calculator: { ...(investment.calculator || {}), margin_percent: v } })} />
              <CalcField label="Total Annual Investment" value={investment.calculator?.annual_investment ?? 0} suffix="EUR" onChange={v => up("investment", { ...investment, calculator: { ...(investment.calculator || {}), annual_investment: v } })} />
              <div className="flex gap-3 pt-2">
                <ToggleSwitch label="Show breakdown" checked={investment.show_breakdown !== false} onChange={v => up("investment", { ...investment, show_breakdown: v })} />
                <ToggleSwitch label="Show ROI" checked={investment.roi_enabled === true} onChange={v => up("investment", { ...investment, roi_enabled: v })} />
              </div>
            </div>
            {/* Live Preview */}
            <div className="rounded-2xl border border-[#C9A96E]/15 bg-gradient-to-br from-[#C9A96E]/5 to-transparent p-6">
              <h3 className="text-[#C9A96E] font-semibold text-sm mb-4 flex items-center gap-2"><TrendingUp className="w-4 h-4" />Recovery Preview</h3>
              <ROIPreview calc={investment.calculator || {}} />
            </div>
          </div>

          {/* ROI Scenarios */}
          {investment.roi_enabled && (
            <div className="space-y-4">
              <h3 className="text-white font-semibold text-sm">ROI Scenarios</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {(investment.roi_scenarios || []).map((sc, i) => (
                  <div key={i} className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5">
                    <input value={sc.label_en || ""} onChange={e => { const s = [...(investment.roi_scenarios || [])]; s[i] = { ...s[i], label_en: e.target.value }; up("investment", { ...investment, roi_scenarios: s }); }}
                      className="bg-transparent text-white/50 text-xs uppercase tracking-wider border-none outline-none w-full mb-3 font-semibold" placeholder="Scenario label" />
                    <div className="space-y-3">
                      <CalcField label="Bookings / Year" value={sc.bookings || 0} suffix="" small onChange={v => { const s = [...(investment.roi_scenarios || [])]; s[i] = { ...s[i], bookings: v }; up("investment", { ...investment, roi_scenarios: s }); }} />
                      <CalcField label="Avg Booking Value" value={sc.avg_value || 0} suffix="EUR" small onChange={v => { const s = [...(investment.roi_scenarios || [])]; s[i] = { ...s[i], avg_value: v }; up("investment", { ...investment, roi_scenarios: s }); }} />
                      <div className="pt-2 text-center">
                        <span className="text-[#C9A96E] text-xl font-light">EUR {((sc.bookings || 0) * (sc.avg_value || 0)).toLocaleString("de-DE")}</span>
                        <p className="text-white/20 text-[10px] mt-1">projected revenue</p>
                      </div>
                    </div>
                    <button onClick={() => up("investment", { ...investment, roi_scenarios: (investment.roi_scenarios || []).filter((_, j) => j !== i) })}
                      className="mt-3 text-white/10 hover:text-red-400 text-[10px] transition-colors"><Trash2 className="w-3 h-3 inline mr-1" />Remove</button>
                  </div>
                ))}
              </div>
              <button onClick={() => up("investment", { ...investment, roi_scenarios: [...(investment.roi_scenarios || []), { label_en: "", label_de: "", bookings: 0, avg_value: 0 }] })}
                className="text-[#C9A96E]/50 text-sm hover:text-[#C9A96E] transition-colors"><Plus className="w-4 h-4 inline mr-1" />Add Scenario</button>
            </div>
          )}
        </div>
      )}

      {/* ── AI IMPORT ── */}
      {activeTab === "import" && <GPTImportTab token={token} onApply={(data) => {
        if (data.name) setEditing(p => ({ ...p, name: data.name, description: data.description || p.description, category: data.category || p.category }));
        if (data.sections) setEditing(p => ({ ...p, sections: { ...p.sections, ...data.sections } }));
        if (data.scraped_media?.images?.length) setEditing(p => ({ ...p, hero_images: data.scraped_media.images }));
        if (data.scraped_media?.video_url) setEditing(p => ({ ...p, video_url: data.scraped_media.video_url }));
        if (data.scraped_media?.logo_url) setEditing(p => ({ ...p, logo_url: data.scraped_media.logo_url }));
        toast.success("AI import applied! Review and adjust all sections.");
        setActiveTab("foundation");
      }} />}

      {/* ── SETTINGS ── */}
      {activeTab === "settings" && (
        <div className="space-y-6 max-w-3xl">
          <SectionHeader title="CTA & Settings" sub="Call-to-action buttons and labels for the public proposal view." icon={Settings2} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <BigInput label="Primary CTA (EN)" value={settings.cta_primary_en || ""} onChange={v => up("settings", { ...settings, cta_primary_en: v })} />
            <BigInput label="Primary CTA (DE)" value={settings.cta_primary_de || ""} onChange={v => up("settings", { ...settings, cta_primary_de: v })} />
            <BigInput label="Secondary CTA (EN)" value={settings.cta_secondary_en || ""} onChange={v => up("settings", { ...settings, cta_secondary_en: v })} />
            <BigInput label="Secondary CTA (DE)" value={settings.cta_secondary_de || ""} onChange={v => up("settings", { ...settings, cta_secondary_de: v })} />
            <BigInput label="Call CTA (EN)" value={settings.cta_call_en || ""} onChange={v => up("settings", { ...settings, cta_call_en: v })} />
            <BigInput label="Call CTA (DE)" value={settings.cta_call_de || ""} onChange={v => up("settings", { ...settings, cta_call_de: v })} />
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════
   REUSABLE COMPONENTS
   ═══════════════════════════════════════════════════ */

function TemplateCard({ t, onEdit, onDelete, onDuplicate, onSetDefault }) {
  const sec = t.sections || {};
  const tiers = sec.foundation?.tiers || [];
  return (
    <div className="group relative rounded-2xl border border-white/[0.06] bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/10 transition-all overflow-hidden" data-testid={`template-card-${t.id}`}>
      {t.is_default && <div className="absolute top-0 left-0 right-0 h-0.5 bg-[#C9A96E]" />}
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-white font-bold text-base">{t.name}</h3>
            {t.description && <p className="text-white/35 text-xs mt-1 line-clamp-2">{t.description}</p>}
          </div>
          {t.is_default && <span className="px-2.5 py-1 bg-[#C9A96E]/15 text-[#C9A96E] text-[10px] font-bold uppercase tracking-wider rounded-full shrink-0">Default</span>}
        </div>
        {tiers.length > 0 && (
          <div className="flex gap-2 mb-4">
            {tiers.map(ti => (
              <div key={ti.id} className={`flex-1 text-center py-2.5 rounded-xl border ${ti.is_recommended ? "border-[#C9A96E]/30 bg-[#C9A96E]/8" : "border-white/[0.06] bg-white/[0.02]"}`}>
                <div className="text-white/70 text-[10px] font-semibold uppercase tracking-wider">{ti.name}</div>
                <div className="text-white/40 text-xs mt-0.5">EUR {(ti.price || 0).toLocaleString("de-DE")}</div>
              </div>
            ))}
          </div>
        )}
        <div className="flex flex-wrap gap-1.5">
          {sec.story_activation?.enabled && <Pill icon={Mic}>Story</Pill>}
          {sec.onsite?.visit?.enabled && <Pill icon={MapPin}>Visit</Pill>}
          {sec.onsite?.production?.enabled && <Pill icon={Camera}>Production</Pill>}
          {sec.distribution?.enabled && <Pill icon={Globe}>Distribution</Pill>}
          {sec.recommended_setup?.enabled && <Pill icon={Sparkles}>Recommended</Pill>}
        </div>
      </div>
      <div className="flex items-center gap-1 px-4 py-3 border-t border-white/[0.04] opacity-0 group-hover:opacity-100 transition-opacity">
        <button onClick={onEdit} className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-white/50 hover:text-white hover:bg-white/5 rounded-lg transition-all" data-testid={`edit-template-${t.id}`}><Edit2 className="w-3 h-3" />Edit</button>
        <button onClick={onDuplicate} className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-white/50 hover:text-white hover:bg-white/5 rounded-lg transition-all"><Copy className="w-3 h-3" />Duplicate</button>
        {!t.is_default && <button onClick={onSetDefault} className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-white/50 hover:text-[#C9A96E] hover:bg-[#C9A96E]/5 rounded-lg transition-all"><Star className="w-3 h-3" />Default</button>}
        <div className="flex-1" />
        <button onClick={onDelete} className="p-1.5 text-white/20 hover:text-red-400 hover:bg-red-500/5 rounded-lg transition-all"><Trash2 className="w-3.5 h-3.5" /></button>
      </div>
    </div>
  );
}

function Pill({ icon: Icon, children }) {
  return <span className="flex items-center gap-1 px-2 py-1 rounded-md bg-white/[0.04] text-white/30 text-[10px] font-medium"><Icon className="w-2.5 h-2.5" />{children}</span>;
}

function SectionHeader({ title, sub, icon: Icon }) {
  return (
    <div className="flex items-start gap-3">
      {Icon && <div className="w-10 h-10 rounded-xl bg-[#C9A96E]/10 flex items-center justify-center shrink-0 mt-0.5"><Icon className="w-5 h-5 text-[#C9A96E]" /></div>}
      <div><h2 className="text-lg font-bold text-white">{title}</h2>{sub && <p className="text-white/35 text-sm mt-1">{sub}</p>}</div>
    </div>
  );
}

function BigInput({ label, value, onChange, placeholder, textarea, testId }) {
  const cls = "w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3.5 text-white text-sm placeholder:text-white/15 focus:border-[#C9A96E]/40 focus:outline-none transition-colors";
  return (
    <div>
      {label && <label className="block text-white/50 text-xs font-semibold uppercase tracking-wider mb-2">{label}</label>}
      {textarea ? <textarea value={value || ""} onChange={e => onChange(e.target.value)} placeholder={placeholder} className={`${cls} resize-none h-24`} data-testid={testId} />
        : <input type="text" value={value || ""} onChange={e => onChange(e.target.value)} placeholder={placeholder} className={cls} data-testid={testId} />}
    </div>
  );
}

function ToggleSwitch({ label, checked, onChange }) {
  return (
    <label className="flex items-center gap-3 cursor-pointer select-none">
      <button type="button" onClick={() => onChange(!checked)}
        className={`relative w-11 h-6 rounded-full transition-colors ${checked ? "bg-[#C9A96E]" : "bg-white/10"}`}>
        <span className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform shadow-sm ${checked ? "translate-x-6" : "translate-x-1"}`} />
      </button>
      <span className="text-white/50 text-sm">{label}</span>
    </label>
  );
}

function StatCard({ label, value, icon: Icon, onChange }) {
  return (
    <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-5 text-center">
      <Icon className="w-5 h-5 text-[#C9A96E]/40 mx-auto mb-3" />
      <input type="number" value={value} onChange={e => onChange(e.target.value)}
        className="bg-transparent text-white text-3xl font-light text-center border-none outline-none w-full" />
      <p className="text-white/25 text-[10px] font-semibold uppercase tracking-wider mt-1">{label}</p>
    </div>
  );
}

function ModuleGrid({ modules, onChange, tiers, showIncluded }) {
  const add = () => {
    const id = `mod_${Date.now()}`;
    onChange([...modules, { id, name_en: "", name_de: "", description_en: "", price: 0, price_range_label: "", enabled: true, included_in_tiers: [], includes: [] }]);
  };
  const upd = (i, f, v) => { const m = [...modules]; m[i] = { ...m[i], [f]: v }; onChange(m); };
  const rm = (i) => onChange(modules.filter((_, j) => j !== i));

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
      {modules.map((m, i) => {
        const Icon = MODULE_ICONS[m.id] || Zap;
        return (
          <div key={m.id || i} className={`relative rounded-2xl border-2 p-5 transition-all ${m.enabled !== false ? "border-white/[0.08] bg-white/[0.02]" : "border-white/[0.04] bg-white/[0.01] opacity-50"}`}>
            <div className="flex items-start justify-between mb-3">
              <div className="w-10 h-10 rounded-xl bg-[#C9A96E]/10 flex items-center justify-center"><Icon className="w-5 h-5 text-[#C9A96E]" /></div>
              <div className="flex items-center gap-1">
                <ToggleSwitch label="" checked={m.enabled !== false} onChange={v => upd(i, "enabled", v)} />
                <button onClick={() => rm(i)} className="p-1 text-white/10 hover:text-red-400 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
            <input value={m.name_en || ""} onChange={e => upd(i, "name_en", e.target.value)}
              className="bg-transparent text-white font-semibold text-sm border-none outline-none w-full mb-1 placeholder:text-white/15" placeholder="Module name" />
            <textarea value={m.description_en || ""} onChange={e => upd(i, "description_en", e.target.value)}
              className="bg-transparent text-white/40 text-xs border-none outline-none w-full resize-none h-10 mb-3 placeholder:text-white/10" placeholder="Benefit-oriented description..." />
            <div className="flex items-center gap-3 mb-3">
              <div className="flex items-center gap-1">
                <DollarSign className="w-3 h-3 text-[#C9A96E]/40" />
                <input type="number" value={m.price || 0} onChange={e => upd(i, "price", parseFloat(e.target.value) || 0)}
                  className="bg-transparent text-white text-sm border-none outline-none w-20" />
              </div>
              <input value={m.price_range_label || ""} onChange={e => upd(i, "price_range_label", e.target.value)}
                className="bg-transparent text-white/25 text-[10px] border-none outline-none flex-1 placeholder:text-white/10" placeholder="e.g. 5,000 – 8,000" />
            </div>
            {tiers.length > 0 && (
              <div className="flex flex-wrap gap-1.5 pt-3 border-t border-white/[0.04]">
                <span className="text-white/15 text-[9px] font-semibold uppercase mr-1 self-center">Tiers:</span>
                {tiers.map(t => (
                  <button key={t.id} onClick={() => { const c = m.included_in_tiers || []; upd(i, "included_in_tiers", c.includes(t.id) ? c.filter(x => x !== t.id) : [...c, t.id]); }}
                    className={`px-2 py-0.5 rounded text-[10px] font-medium transition-all ${
                      (m.included_in_tiers || []).includes(t.id) ? "bg-[#C9A96E]/20 text-[#C9A96E]" : "bg-white/[0.03] text-white/20 hover:text-white/40"}`}>
                    {t.name}
                  </button>
                ))}
              </div>
            )}
          </div>
        );
      })}
      <button onClick={add} className="flex flex-col items-center justify-center min-h-[200px] rounded-2xl border-2 border-dashed border-white/[0.06] text-white/15 hover:border-[#C9A96E]/25 hover:text-[#C9A96E] transition-all cursor-pointer">
        <Plus className="w-6 h-6 mb-2" /><span className="text-xs font-medium">Add Module</span>
      </button>
    </div>
  );
}

function CalcField({ label, value, suffix, onChange, small }) {
  return (
    <div className="flex items-center justify-between gap-3">
      <label className={`text-white/40 ${small ? "text-xs" : "text-sm"} shrink-0`}>{label}</label>
      <div className="flex items-center gap-2">
        <input type="number" value={value} onChange={e => onChange(parseFloat(e.target.value) || 0)}
          className={`bg-white/[0.04] border border-white/[0.06] rounded-lg text-right text-white focus:border-[#C9A96E]/30 focus:outline-none ${small ? "w-20 px-2 py-1.5 text-xs" : "w-28 px-3 py-2 text-sm"}`} />
        {suffix && <span className="text-white/25 text-xs w-8">{suffix}</span>}
      </div>
    </div>
  );
}

function ROIPreview({ calc }) {
  const adr = calc.adr || 350;
  const nights = calc.avg_stay_nights || 3;
  const margin = (calc.margin_percent || 30) / 100;
  const investment = calc.annual_investment || 0;
  const revenuePerBooking = adr * nights;
  const marginPerBooking = revenuePerBooking * margin;
  const bookingsNeeded = investment > 0 && marginPerBooking > 0 ? Math.ceil(investment / marginPerBooking) : 0;
  const monthlyBookings = bookingsNeeded > 0 ? (bookingsNeeded / 12).toFixed(1) : 0;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <MiniStat label="Revenue / Booking" value={`EUR ${revenuePerBooking.toLocaleString("de-DE")}`} />
        <MiniStat label="Margin / Booking" value={`EUR ${Math.round(marginPerBooking).toLocaleString("de-DE")}`} />
        <MiniStat label="Bookings to Recover" value={bookingsNeeded || "—"} highlight />
        <MiniStat label="Monthly Needed" value={monthlyBookings || "—"} />
      </div>
      {investment > 0 && bookingsNeeded > 0 && (
        <p className="text-[#C9A96E]/60 text-xs leading-relaxed">
          With an average booking value of EUR {revenuePerBooking.toLocaleString("de-DE")} and {Math.round(margin * 100)}% margin, the property needs approximately <strong className="text-[#C9A96E]">{bookingsNeeded} bookings/year</strong> (~{monthlyBookings}/month) to recover the investment.
        </p>
      )}
    </div>
  );
}

function MiniStat({ label, value, highlight }) {
  return (
    <div className="text-center py-3 rounded-xl bg-white/[0.03]">
      <p className={`text-lg font-light ${highlight ? "text-[#C9A96E]" : "text-white/70"}`}>{value}</p>
      <p className="text-white/20 text-[10px] mt-0.5">{label}</p>
    </div>
  );
}

function GPTImportTab({ token, onApply }) {
  const [text, setText] = useState("");
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  const handleImport = async () => {
    if (!text.trim()) { toast.error("Paste some text first"); return; }
    setLoading(true);
    try {
      const body = { text, context: "template" };
      if (url.trim()) body.url = url.trim();
      const { data } = await axios.post(`${API}/templates/import-gpt`, body, { headers: { Authorization: `Bearer ${token}` } });
      setPreview(data);
      toast.success("Parsed! Review the preview below.");
    } catch (e) { toast.error(e.response?.data?.detail || "Import failed"); }
    setLoading(false);
  };

  const tiers = preview?.sections?.foundation?.tiers || [];
  const features = preview?.sections?.foundation?.comparison_features || [];
  const storyMods = preview?.sections?.story_activation?.modules || [];
  const distMods = preview?.sections?.distribution?.modules || [];
  const evalMods = preview?.sections?.onsite?.evaluation?.modules || [];
  const prodMods = preview?.sections?.onsite?.production?.modules || [];
  const visit = preview?.sections?.onsite?.visit || {};
  const rec = preview?.sections?.recommended_setup || {};
  const settings = preview?.sections?.settings || {};
  const media = preview?.scraped_media || {};

  return (
    <div className="space-y-6 max-w-5xl">
      <SectionHeader title="AI Quick Import" sub="Paste structured text from ChatGPT or planning documents. Optionally add a hotel URL to fetch images and videos." icon={Wand2} />
      
      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-6 space-y-4">
        <textarea value={text} onChange={e => setText(e.target.value)} placeholder={`Paste your template/proposal structure here...\n\nExample:\nFOUNDATION TIERS:\n- Signature | 12000 | Recommended: no\n- Prestige | 24000 | Recommended: yes\n\nSTORY ACTIVATION:\nMODULES:\n- Podcast Episode | 3500 | tiers: Signature,Prestige | active: yes\n\nONSITE VISIT:\nTEAM SIZE: 4\nNIGHTS: 5\n...`}
          className="w-full bg-white/[0.03] border border-white/[0.06] rounded-xl px-4 py-3 text-white text-sm resize-none h-56 focus:border-[#C9A96E]/30 focus:outline-none placeholder:text-white/15 font-mono leading-relaxed" data-testid="gpt-import-textarea" />
        
        <div>
          <label className="block text-white/40 text-xs font-semibold uppercase tracking-wider mb-2">Hotel URL (optional — fetches images & video)</label>
          <input type="url" value={url} onChange={e => setUrl(e.target.value)} placeholder="https://www.hotel-website.com/..."
            className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3 text-white text-sm focus:border-[#C9A96E]/40 focus:outline-none placeholder:text-white/15" data-testid="gpt-import-url" />
        </div>
        
        <button onClick={handleImport} disabled={loading}
          className="flex items-center gap-2 px-6 py-3 bg-[#C9A96E] text-[#030303] rounded-xl text-sm font-bold hover:bg-[#D4B87A] disabled:opacity-50 transition-all" data-testid="gpt-import-btn">
          <Wand2 className="w-4 h-4" />{loading ? "Parsing & fetching..." : "Parse with AI"}
        </button>
      </div>

      {/* Preview */}
      {preview && (
        <div className="space-y-5">
          <div className="flex items-center justify-between">
            <h3 className="text-[#C9A96E] font-bold text-base">Import Preview</h3>
            <button onClick={() => onApply(preview)} className="flex items-center gap-2 px-6 py-2.5 bg-[#C9A96E] text-[#030303] rounded-xl text-sm font-bold hover:bg-[#D4B87A] transition-all" data-testid="gpt-apply-btn">
              <Check className="w-4 h-4" />Apply to Template
            </button>
          </div>

          {/* Name + Category */}
          <div className="rounded-2xl border border-[#C9A96E]/20 bg-[#C9A96E]/5 p-5">
            <p className="text-white font-semibold">{preview.name || "—"}</p>
            {preview.description && <p className="text-white/40 text-sm mt-1">{preview.description}</p>}
          </div>

          {/* Tiers */}
          {tiers.length > 0 && (
            <div>
              <h4 className="text-white/50 text-xs font-semibold uppercase tracking-wider mb-3">Foundation Tiers</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {tiers.map(t => (
                  <div key={t.id} className={`rounded-xl border p-4 text-center ${t.is_recommended ? "border-[#C9A96E]/40 bg-[#C9A96E]/8" : "border-white/[0.08] bg-white/[0.02]"}`}>
                    {t.is_recommended && <span className="text-[#C9A96E] text-[10px] font-bold uppercase">Recommended</span>}
                    <p className="text-white font-bold text-sm">{t.name}</p>
                    <p className="text-white/50 text-lg">EUR {(t.price || 0).toLocaleString("de-DE")}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Features */}
          {features.length > 0 && (
            <div>
              <h4 className="text-white/50 text-xs font-semibold uppercase tracking-wider mb-2">Comparison Features ({features.length})</h4>
              <div className="flex flex-wrap gap-1.5">
                {features.map((f, i) => <span key={i} className="px-2.5 py-1 rounded-lg bg-white/[0.04] text-white/50 text-xs">{f.name_en}</span>)}
              </div>
            </div>
          )}

          {/* Story + Distribution + Evaluation + Production Modules */}
          {[
            { title: "Story Activation", mods: storyMods },
            { title: "Distribution", mods: distMods },
            { title: "On-Site Evaluation", mods: evalMods },
            { title: "On-Site Production", mods: prodMods },
          ].filter(g => g.mods.length > 0).map(g => (
            <div key={g.title}>
              <h4 className="text-white/50 text-xs font-semibold uppercase tracking-wider mb-2">{g.title} ({g.mods.length} modules)</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {g.mods.map((m, i) => (
                  <div key={i} className="flex items-center justify-between px-3 py-2 rounded-lg border border-white/[0.06] bg-white/[0.02]">
                    <span className="text-white/60 text-sm">{m.name_en}</span>
                    <span className="text-white/30 text-xs">EUR {(m.price || 0).toLocaleString("de-DE")}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}

          {/* Visit */}
          {visit.enabled && (
            <div>
              <h4 className="text-white/50 text-xs font-semibold uppercase tracking-wider mb-2">On-Site Visit</h4>
              <div className="flex gap-3 flex-wrap">
                <span className="px-3 py-1.5 rounded-lg bg-white/[0.04] text-white/50 text-xs">Team: {visit.team_size}</span>
                <span className="px-3 py-1.5 rounded-lg bg-white/[0.04] text-white/50 text-xs">Nights: {visit.nights}</span>
                <span className="px-3 py-1.5 rounded-lg bg-white/[0.04] text-white/50 text-xs">{visit.hosted_by_property ? "Hosted" : "Self-Arranged"}</span>
                <span className="px-3 py-1.5 rounded-lg bg-white/[0.04] text-white/50 text-xs">Travel: {visit.travel_expense_mode?.replace(/_/g, " ")}</span>
              </div>
              {visit.note_en && <p className="text-white/25 text-xs mt-2 italic">{visit.note_en}</p>}
            </div>
          )}

          {/* Recommended */}
          {rec.enabled && (
            <div>
              <h4 className="text-white/50 text-xs font-semibold uppercase tracking-wider mb-2">Recommended Setup</h4>
              <p className="text-white/40 text-sm">{rec.description_en || rec.label_en}</p>
            </div>
          )}

          {/* Settings */}
          {settings.cta_primary_en && (
            <div>
              <h4 className="text-white/50 text-xs font-semibold uppercase tracking-wider mb-2">CTAs</h4>
              <div className="flex gap-2 flex-wrap">
                <span className="px-3 py-1.5 rounded-lg bg-[#C9A96E]/10 text-[#C9A96E] text-xs">{settings.cta_primary_en}</span>
                {settings.cta_secondary_en && <span className="px-3 py-1.5 rounded-lg bg-white/[0.04] text-white/50 text-xs">{settings.cta_secondary_en}</span>}
                {settings.cta_call_en && <span className="px-3 py-1.5 rounded-lg bg-white/[0.04] text-white/50 text-xs">{settings.cta_call_en}</span>}
              </div>
            </div>
          )}

          {/* Scraped Media */}
          {media.images?.length > 0 && (
            <div>
              <h4 className="text-white/50 text-xs font-semibold uppercase tracking-wider mb-3">Fetched Images ({media.images.length})</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
                {media.images.map((img, i) => (
                  <div key={i} className="aspect-[4/3] rounded-lg overflow-hidden border border-white/[0.06] bg-white/[0.02]">
                    <img src={img} alt="" className="w-full h-full object-cover" onError={e => { e.target.style.display = "none"; }} />
                  </div>
                ))}
              </div>
            </div>
          )}
          {media.video_url && (
            <div>
              <h4 className="text-white/50 text-xs font-semibold uppercase tracking-wider mb-2">Video Found</h4>
              <a href={media.video_url} target="_blank" rel="noopener noreferrer" className="text-[#C9A96E] text-sm underline">{media.video_url}</a>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ─── Helper: collect all modules across sections ─── */
function getAllModules(sections) {
  const all = [];
  (sections?.story_activation?.modules || []).forEach(m => all.push(m));
  (sections?.onsite?.evaluation?.modules || []).forEach(m => all.push(m));
  (sections?.onsite?.production?.modules || []).forEach(m => all.push(m));
  (sections?.distribution?.modules || []).forEach(m => all.push(m));
  return all;
}
